import { useEffect, useState } from "react";
import { translateBatch } from "../services/translateService";

const BASE_LANG = "es";

// Envuelve un diccionario de textos estilo { es: {...}, en: {...} } usado en
// cada pantalla. Si el idioma activo ya tiene traducciones estáticas (es/en)
// se usan tal cual, igual que antes. Si no (cualquiera de los otros ~70
// idiomas del selector), traduce en vivo las frases en español vía Cloud
// Function la primera vez que se necesitan, con cache compartida entre
// todos los usuarios — mientras llega, muestra el texto en español para que
// la pantalla nunca se vea vacía ni a medio traducir.
//
// Importante: no dedupear la solicitud con un ref persistente entre
// invocaciones del efecto. En desarrollo, React.StrictMode monta cada
// efecto, lo limpia y lo vuelve a montar una vez para detectar bugs — con
// un ref persistente, la segunda invocación (la que realmente sobrevive)
// veía "ya se pidió" y no arrancaba nada, mientras que la primera (la que
// sí pidió la traducción) ya había sido cancelada por la limpieza cuando
// la respuesta llegaba. Resultado: la traducción se calculaba y quedaba
// en cache, pero el estado del componente nunca se actualizaba. Cada
// invocación del efecto ahora maneja su propia bandera "cancelled" y
// dispara su propio pedido, así la que sobrevive sí actualiza el estado.
export function useTranslatedDictionary(dictionary, language) {
  const [liveTranslations, setLiveTranslations] = useState({});

  useEffect(() => {
    if (!language || language === BASE_LANG || dictionary[language]) {
      return;
    }

    const baseDict = dictionary[BASE_LANG] || {};
    const keys = Object.keys(baseDict).filter((k) => typeof baseDict[k] === "string");
    const texts = keys.map((k) => baseDict[k]);
    if (!texts.length) return;

    let cancelled = false;
    translateBatch(texts, language).then((translated) => {
      if (cancelled) return;
      const map = {};
      keys.forEach((k, i) => {
        map[k] = translated[i];
      });
      setLiveTranslations(map);
    });

    return () => {
      cancelled = true;
    };
  }, [dictionary, language]);

  return (key) => {
    if (dictionary[language]?.[key] !== undefined) return dictionary[language][key];
    if (liveTranslations[key] !== undefined) return liveTranslations[key];
    return dictionary[BASE_LANG]?.[key] || key;
  };
}
